import {
    type BaseNodeCfg,
    type NodeCfg,
    type NodeKind,
    type NodeSocketKind,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { FlowNodeOutput } from '../../sockets/FlowNodeOutput';
import { AsyncComponent } from 'vue';
import type { FlowConnection } from '@redviking/argonaut-util/types/mes/applet-designs/latest/flow/flow.zod';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import type { FlowNodeInput } from '../../sockets/FlowNodeInput';
import type { FlowNodeControl } from '../../controls/FlowNodeControl';

export const NODE_RESIZE_HANDLER_HEIGHT_PX = 16;
export const NODE_TITLE_HEIGHT_PX = 44;
export const NODE_DEFAULT_COMPONENT_HEIGHT_PX = 30;

/**
 * defines the expected controls for a node, given that they operate on that node control's value type.
 */
type NodeControlValues<CFG extends BaseNodeCfg> = CFG extends { controlValues?: Record<string, unknown> }
    ? {
        [controlKey in keyof CFG['controlValues']]: FlowNodeControl.WideOpts<CFG['controlValues'][controlKey]>;
    }
    : Record<string, never>;

export type NodeCategory = 'text' | 'number' | 'variable' | 'events' | 'argonautApi' | 'timeAndDate' | 'other' | 'boolean' | 'tab' | 'material' | 'function' | 'variable' | 'connection' | 'timeAndDate' | 'argonautApi' | 'file';

// FUTURE maybe it would be beneficial to use a type that includes the connection ID?
export interface WideNodeConnections {
    inputs: Record<string, FlowConnection[] | undefined>;
    outputs: Record<string, FlowConnection[] | undefined>;
}

export interface NodeConnections<KIND extends NodeKind> extends WideNodeConnections {
    inputs: {
        [input in keyof NodeSocketKindMap[KIND]['inputs']]?: FlowConnection[];
    };
    outputs: {
        [output in keyof NodeSocketKindMap[KIND]['outputs']]?: FlowConnection[];
    };
}

export type NodeValidationErrors = {
    [control: string]: string;
} | string;

export type NodeValidationResult = true | NodeValidationErrors;

export type NodeValidation<CFG extends BaseNodeCfg> = (node: CFG, appletDesignVersionConfig: Latest.AppletDesignVersionConfig) => NodeValidationResult | Promise<NodeValidationResult>;

/**
 * This type expands the functionality of the return type from RequiredSocket to say that at least one of the sockets in the list has to be wired up.
 * This is helpful for nodes like the EventSwitch node which has two outputs: `true` or `false`. If neither are wired the node is useless so at least one must be wired as part of the validation
 */
type ConditionalOrSocket<SOCKET_NAMES extends string> = {
    kind: 'or'
    availableOptions: SOCKET_NAMES[];
};

/**
 * This type expands the functionality of the return type from RequiredSocket to say that at all listed sockets have to be wired up or none
 * I dont think this has a particular usecase yet, but you could argue that the UdpNode is pretty useless if you **only** wire up the `data received` socket and not the `payload` socket
 */
type ConditionalAndSocket<SOCKET_NAMES extends string> = {
    kind: 'and'
    availableOptions: SOCKET_NAMES[];
};

type ConditionalSocket<SOCKET_NAMES extends string> = ConditionalOrSocket<SOCKET_NAMES> | ConditionalAndSocket<SOCKET_NAMES>;

/**
 * This function dynamically returns which sockets for a given node config are required, this is helpful for nodes that have dynamic sockets (i.e. material create node WRT serial number socket(s))
 */
export type RequiredSockets<KIND extends NodeKind, SOCKETS extends NodeSocketKindMap[KIND]> = (node: NodeCfg<KIND>, availableInputSockets: (keyof SOCKETS['inputs'])[], availableOutputSockets: (keyof SOCKETS['outputs'])[]) => {
    conditionallyRequired: ConditionalSocket<(keyof SOCKETS['inputs'] | keyof SOCKETS['outputs']) & string>[];
    /**
     * socket keys in this list will automatically be checked for a default value
     */
    required: (keyof SOCKETS['inputs'] | keyof SOCKETS['outputs'])[];
};

export type WideRequiredSockets = (nodeCfg: BaseNodeCfg, availableInputSockets: string[], availableOutputSockets: string[]) => {
    conditionallyRequired: ConditionalSocket<string>[];
    required: string[];
}

export type KindLabel<CFG extends BaseNodeCfg> = string | ((cfg: CFG) => string);

export interface BaseOnConnectionUpdate {
    (opts: { nodeCfg: BaseNodeCfg; connection: FlowConnection; nodeConnections: WideNodeConnections }): BaseNodeCfg
}
export interface OnConnectionUpdate<KIND extends NodeKind, CFG extends NodeCfg<KIND>> {
    (opts: { nodeCfg: CFG; connection: FlowConnection; nodeConnections: WideNodeConnections }): CFG
}

export interface NodeSpec<CFG extends BaseNodeCfg> {
    canCopy?: boolean;
    cfg: () => CFG;
    nodeCategory: NodeCategory;
    /**
     * Optional grouping within `nodeCategory` used to nest closely related nodes under a shared submenu in the "Add Node" context menu
     * (e.g. IO Link, Torque Tool, MQTT within the `connection` category). Nodes without a sub-category appear directly under their category.
     */
    nodeSubCategory?: string;
    /**
     * If true, the node will not appear in the flow inspector and cannot be added
     */
    suppressNode?: boolean | (() => boolean);
    /**
     * An extra component can be defined that will appear in the flow inspector when the node is selected to provide additional configuration options
     */
    extraComponent?: (cfg: CFG) => AsyncComponent | null;
    /**
     * Define how to populate control values for the node. Returns a partial object, it is expected that any controls not configured here will be configured in a helper component in the flow inspector
     */
    controls: (cfg: CFG) => Partial<NodeControlValues<CFG>>;
    extraControls?: (cfg: CFG) => Record<string, FlowNodeControl.WideOpts>;
    inputs: (node: CFG) => ({
        [inputKey in keyof NodeSocketKindMap[CFG['kind']]['inputs']]?: FlowNodeInput.Opts<NodeSocketKindMap[CFG['kind']]['inputs'][inputKey] & NodeSocketKind>;
    }),
    /**
     * Define which sockets are required to be wired for the node to work as intended.
     * Use 'all' for all sockets required, 'none' for no sockets required, specify a subset using the key of either an input or an output
     * Or define a custom function if the socket is **dynamicially** required
     */
    requiredSockets: 'all' | 'none' | (keyof NodeSocketKindMap[CFG['kind']]['inputs'] | keyof NodeSocketKindMap[CFG['kind']]['outputs'])[] | RequiredSockets<CFG['kind'], NodeSocketKindMap[CFG['kind']]>;
    kindLabel: KindLabel<CFG>;
    canDelete?: boolean | ((cfg: CFG) => boolean);
    canResize?: boolean | ((cfg: CFG) => boolean);
    outputs: (node: CFG) => ({
        [outputKey in keyof NodeSocketKindMap[CFG['kind']]['outputs']]?: FlowNodeOutput.Opts<NodeSocketKindMap[CFG['kind']]['outputs'][outputKey] & NodeSocketKind>;
    }),
    // Returns true, a map of control errors, or node error.
    validator: NodeValidation<CFG>;
}
